import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { TwitchIcon, YoutubeIcon, KickIcon } from './icons/PlatformIcons';
import { Globe2, Copy, ExternalLink, Check, ArrowLeft } from 'lucide-react';

interface FormData {
  firstName: string;
  email: string;
  discord: string;
  platforms: string[];
  platformUrls: Record<string, string>;
  averageViewers: string;
  monthlyStreams: string;
  currentSetup: string;
  goals: string;
}

const platforms = [
  { id: 'twitch', name: 'Twitch', icon: TwitchIcon },
  { id: 'youtube', name: 'YouTube', icon: YoutubeIcon },
  { id: 'kick', name: 'Kick', icon: KickIcon },
  { id: 'other', name: 'Other', icon: Globe2 },
];

export function PartnerApplication() {
  const [currentStep, setCurrentStep] = useState(0);
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    email: '',
    discord: '',
    platforms: [],
    platformUrls: {},
    averageViewers: '',
    monthlyStreams: '',
    currentSetup: '',
    goals: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDiscordMessage, setShowDiscordMessage] = useState(false);
  const [copied, setCopied] = useState(false);

  const validateStep = () => {
    const newErrors: Record<string, string> = {};

    switch (currentStep) {
      case 0:
        if (!formData.firstName.trim()) newErrors.firstName = 'Required';
        if (!formData.email.trim()) {
          newErrors.email = 'Required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Invalid email';
        }
        if (!formData.discord.trim()) newErrors.discord = 'Required';
        break;
      case 1:
        if (formData.platforms.length === 0) {
          newErrors.platforms = 'Select at least one platform';
        }
        formData.platforms.forEach(platform => {
          if (!formData.platformUrls[platform]) {
            newErrors[`url_${platform}`] = 'Required';
          }
        });
        break;
      case 2:
        if (!formData.averageViewers) newErrors.averageViewers = 'Required';
        if (!formData.monthlyStreams) newErrors.monthlyStreams = 'Required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateDiscordMessage = () => {
    const platformList = formData.platforms
      .map(platform => {
        const url = formData.platformUrls[platform];
        return `${platform.charAt(0).toUpperCase() + platform.slice(1)}: ${url}`;
      })
      .join('\n');

    return `**Partner Application**

**Name:** ${formData.firstName}
**Email:** ${formData.email}
**Discord:** ${formData.discord}

**Platforms:**
${platformList}

**Average Concurrent Viewers:** ${formData.averageViewers}
**IRL Streams per Month:** ${formData.monthlyStreams}

**Current Setup:**
${formData.currentSetup || 'Not provided'}

**Streaming Goals:**
${formData.goals || 'Not provided'}`;
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(generateDiscordMessage());
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Now join Discord and open a ticket to submit.",
      });
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast({
        title: "Copy failed",
        description: "Please select and copy the text manually.",
        variant: "destructive",
      });
    }
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (currentStep === 2) {
      setShowDiscordMessage(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const togglePlatform = (platformId: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  if (showDiscordMessage) {
    return (
      <div className="min-h-screen pt-24 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 mx-auto mb-6 bg-cyan-500/10 border border-cyan-500/30 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-cyan-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Application Ready
            </h1>
            <p className="text-gray-400">
              Copy your message and submit it via Discord
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-400">Your Application</span>
              <Button
                size="sm"
                onClick={handleCopyMessage}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-medium"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 font-mono text-sm text-gray-300 whitespace-pre-wrap max-h-80 overflow-y-auto">
              {generateDiscordMessage()}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 mb-8"
          >
            <h3 className="text-white font-medium mb-4">Next Steps</h3>
            <ol className="space-y-3 text-gray-400 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold flex items-center justify-center">1</span>
                Copy your application using the button above
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold flex items-center justify-center">2</span>
                Join our Discord server
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold flex items-center justify-center">3</span>
                Open a ticket and paste your application
              </li>
            </ol>
          </motion.div>

          <div className="flex gap-4">
            <Link to="/partners">
              <Button
                variant="outline"
                className="border-gray-700 text-white hover:bg-gray-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <Button
              onClick={() => window.open('https://discord.gg/irltools', '_blank')}
              className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Join Discord
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/partners" className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Partners
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Partner Application
          </h1>
          <p className="text-gray-400">
            Step {currentStep + 1} of 3
          </p>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {[0, 1, 2].map((step) => (
            <div
              key={step}
              className={`h-1 flex-1 rounded-full transition-colors ${
                step <= currentStep ? 'bg-cyan-500' : 'bg-gray-800'
              }`}
            />
          ))}
        </div>

        {/* Form */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 md:p-8 mb-8"
        >
          {currentStep === 0 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-white mb-6">Contact Information</h2>
              <div>
                <Label htmlFor="firstName" className="text-gray-300 text-sm">First Name</Label>
                <Input
                  id="firstName"
                  placeholder="Your name"
                  value={formData.firstName}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  className="mt-1 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600"
                />
                {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
              </div>
              <div>
                <Label htmlFor="email" className="text-gray-300 text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600"
                />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="discord" className="text-gray-300 text-sm">Discord Username</Label>
                <Input
                  id="discord"
                  placeholder="username"
                  value={formData.discord}
                  onChange={(e) => setFormData(prev => ({ ...prev, discord: e.target.value }))}
                  className="mt-1 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600"
                />
                {errors.discord && <p className="text-red-400 text-xs mt-1">{errors.discord}</p>}
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-white mb-6">Streaming Platforms</h2>
              <div className="grid grid-cols-2 gap-3">
                {platforms.map((platform) => {
                  const Icon = platform.icon;
                  const selected = formData.platforms.includes(platform.id);
                  return (
                    <button
                      key={platform.id}
                      type="button"
                      onClick={() => togglePlatform(platform.id)}
                      className={`p-4 rounded-xl border text-left transition-colors ${
                        selected
                          ? 'bg-cyan-500/10 border-cyan-500/50'
                          : 'bg-gray-950 border-gray-800 hover:border-gray-700'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mb-2 ${selected ? 'text-cyan-400' : 'text-gray-400'}`} />
                      <span className={`text-sm font-medium ${selected ? 'text-white' : 'text-gray-400'}`}>
                        {platform.name}
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.platforms && <p className="text-red-400 text-xs">{errors.platforms}</p>}

              {formData.platforms.length > 0 && (
                <div className="space-y-4 pt-4 border-t border-gray-800">
                  <p className="text-sm text-gray-400">Enter your channel URLs:</p>
                  {formData.platforms.map((platformId) => {
                    const platform = platforms.find(p => p.id === platformId);
                    return (
                      <div key={platformId}>
                        <Label className="text-gray-300 text-sm">{platform?.name} URL</Label>
                        <Input
                          placeholder={`https://${platformId === 'other' ? 'example' : platformId}.com/...`}
                          value={formData.platformUrls[platformId] || ''}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            platformUrls: { ...prev.platformUrls, [platformId]: e.target.value }
                          }))}
                          className="mt-1 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600"
                        />
                        {errors[`url_${platformId}`] && (
                          <p className="text-red-400 text-xs mt-1">{errors[`url_${platformId}`]}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-white mb-6">Streaming Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 text-sm">Average Viewers</Label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={formData.averageViewers}
                    onChange={(e) => setFormData(prev => ({ ...prev, averageViewers: e.target.value }))}
                    className="mt-1 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600"
                  />
                  {errors.averageViewers && <p className="text-red-400 text-xs mt-1">{errors.averageViewers}</p>}
                </div>
                <div>
                  <Label className="text-gray-300 text-sm">IRL Streams/Month</Label>
                  <Input
                    type="number"
                    placeholder="8"
                    value={formData.monthlyStreams}
                    onChange={(e) => setFormData(prev => ({ ...prev, monthlyStreams: e.target.value }))}
                    className="mt-1 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600"
                  />
                  {errors.monthlyStreams && <p className="text-red-400 text-xs mt-1">{errors.monthlyStreams}</p>}
                </div>
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Current Setup (optional)</Label>
                <Textarea
                  placeholder="Describe your current streaming setup..."
                  value={formData.currentSetup}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentSetup: e.target.value }))}
                  className="mt-1 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600 min-h-[100px]"
                />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Streaming Goals (optional)</Label>
                <Textarea
                  placeholder="What are your streaming goals?"
                  value={formData.goals}
                  onChange={(e) => setFormData(prev => ({ ...prev, goals: e.target.value }))}
                  className="mt-1 bg-gray-950 border-gray-800 text-white placeholder:text-gray-600 min-h-[100px]"
                />
              </div>
            </div>
          )}
        </motion.div>

        {/* Navigation */}
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={currentStep === 0}
            className="border-gray-700 text-white hover:bg-gray-800 disabled:opacity-30"
          >
            Back
          </Button>
          <Button
            onClick={handleNext}
            className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
          >
            {currentStep === 2 ? 'Generate Application' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}